import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  alpha,
  useTheme,
} from "@mui/material";
import { Clear, Search } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";

export type TextFilterMode = "contains" | "exact";

interface TextFilterProps {
  /** Current filter value */
  value: string;

  /** Callback when value changes */
  onChange: (value: string) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Debounce delay in ms */
  debounceMs?: number;

  /** Whether to show the search icon */
  showSearchIcon?: boolean;

  /** Whether to show the clear button */
  showClearButton?: boolean;

  /** Label for the input */
  label?: string;

  /** Match mode: contains (ILIKE) or exact (=) */
  mode?: TextFilterMode;

  /** Callback when mode changes */
  onModeChange?: (mode: TextFilterMode) => void;
}

/**
 * Text filter with search/contains functionality
 *
 * Features:
 * - Debounced input
 * - Clear button
 * - Search icon
 * - Enter to apply
 */
const TextFilter: React.FC<TextFilterProps> = ({
  value,
  onChange,
  placeholder = "Search...",
  debounceMs = 500,
  showSearchIcon = true,
  showClearButton = true,
  label,
  mode = "contains",
  onModeChange,
}) => {
  const theme = useTheme();
  const [localValue, setLocalValue] = useState(value);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onChangeRef = useRef(onChange);

  // Keep callback ref up to date (avoids recreating debounced function)
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync with external value (only if different to avoid cursor issues)
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced callback
    debounceTimerRef.current = setTimeout(() => {
      onChangeRef.current(newValue);
    }, debounceMs);
  };

  const handleClear = () => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setLocalValue("");
    onChangeRef.current("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Apply immediately on Enter
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      onChangeRef.current(localValue);
    } else if (e.key === "Escape") {
      handleClear();
    }
  };

  return (
    <TextField
      size="small"
      fullWidth
      value={localValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      label={label}
      InputProps={{
        startAdornment: showSearchIcon ? (
          <InputAdornment position="start">
            <Search
              sx={{
                fontSize: "1.125rem",
                color: theme.palette.text.disabled,
              }}
            />
          </InputAdornment>
        ) : undefined,
        endAdornment: (
          <InputAdornment position="end" sx={{ gap: 0.25 }}>
            {showClearButton && localValue && (
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{
                  p: 0.25,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.action.hover, 0.5),
                  },
                }}
              >
                <Clear sx={{ fontSize: "1rem" }} />
              </IconButton>
            )}
            {onModeChange && (
              <Select
                size="small"
                value={mode}
                onChange={(e) => onModeChange(e.target.value as TextFilterMode)}
                variant="standard"
                disableUnderline
                sx={{
                  fontSize: "0.6875rem",
                  color: theme.palette.text.secondary,
                  minWidth: 70,
                  "& .MuiSelect-select": {
                    py: 0.25,
                    pr: "18px !important",
                    pl: 0.5,
                  },
                  "& .MuiSelect-icon": {
                    fontSize: "1rem",
                    right: 0,
                  },
                }}
              >
                <MenuItem value="contains" sx={{ fontSize: "0.75rem" }}>
                  Contains
                </MenuItem>
                <MenuItem value="exact" sx={{ fontSize: "0.75rem" }}>
                  Exact
                </MenuItem>
              </Select>
            )}
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          fontSize: "0.8125rem",
          backgroundColor: alpha(theme.palette.background.default, 0.5),
        },
        "& .MuiOutlinedInput-input": {
          py: 0.875,
        },
      }}
    />
  );
};

export default TextFilter;
